/**
 * resolveStudioSectionList — single resolver for Template Studio section
 * lists (Sections tab, preview pane, export parity).
 *
 * Modes:
 *  - `per-type`: sectionsByReportType[type] → feasibility flat legacy → type defaults
 *  - `flat-default`: template.sections → feasibility defaults; optional filter by sample type
 */
import type { ReportTypeId } from '../config/reportTypes';
import {
  getDefaultSectionConfigs,
  getDefaultSectionConfigsForType,
  getSectionsForType,
} from '../config/reportSections';
import {
  rendererSectionById,
  subSectionsForParent,
  SUB_SECTION_IDS,
} from '../config/rendererSectionRegistry';
import { coerceSectionConfigList } from './reportTemplateNormalization';
import { isCustomBriefQuestionSectionId } from './customBriefSections';
import { projectTemplateSectionIds } from './effectiveTemplateConfig';
import type { ReportTemplate, SectionConfig } from '../types/reportTemplate';

export type StudioSectionListMode = 'flat-default' | 'per-type';

export interface ResolveStudioSectionListInput {
  template: ReportTemplate | null;
  reportType: ReportTypeId;
  mode: StudioSectionListMode;
  /** In flat-default mode, keep only ids valid for this report type (+ sub-sections). */
  filterReportType?: ReportTypeId | null;
  /** Insert sub-section configs after their parent when absent (Studio default). */
  augmentSubSections?: boolean;
}

/** Whether `sectionId` belongs on a report of `reportType` given the current list. */
export function isSectionIdValidForReportType(
  sectionId: string,
  reportType: ReportTypeId,
  sectionIdsInList: readonly string[],
): boolean {
  const canonicalIds = new Set(getSectionsForType(reportType).map(s => s.sectionId));
  if (canonicalIds.has(sectionId)) return true;
  if (SUB_SECTION_IDS.has(sectionId)) {
    const entry = rendererSectionById(sectionId);
    if (entry?.parentSectionId && sectionIdsInList.includes(entry.parentSectionId)) {
      return true;
    }
  }
  if (isCustomBriefQuestionSectionId(sectionId)) return reportType === 'custom-brief';
  return false;
}

function filterFlatSectionsForType(
  sections: SectionConfig[],
  reportType: ReportTypeId,
  template: ReportTemplate,
): SectionConfig[] {
  const ids = sections.map(s => s.sectionId);
  const customIds = new Set(
    (template.customSections ?? [])
      .filter(cs =>
        cs.scope === 'all'
        || (Array.isArray(cs.scope) && cs.scope.includes(reportType)),
      )
      .map(cs => cs.id),
  );
  return sections.filter(s =>
    isSectionIdValidForReportType(s.sectionId, reportType, ids)
    || customIds.has(s.sectionId),
  );
}

/** Insert sub-section configs after their parent when absent from the list. */
export function augmentWithSubSections(sections: SectionConfig[]): SectionConfig[] {
  const out = [...sections];
  const seen = new Set(out.map(s => s.sectionId));
  for (let i = 0; i < out.length; i++) {
    const parentId = out[i].sectionId;
    for (const sub of subSectionsForParent(parentId)) {
      if (seen.has(sub.id)) continue;
      const entry: SectionConfig = {
        sectionId: sub.id,
        type: 'builtin',
        visible: true,
        columnLayout: 1,
        page: out[i].page,
      };
      out.splice(i + 1, 0, entry);
      seen.add(sub.id);
      i++;
    }
  }
  return out;
}

function fallbackBuiltinSection(sectionId: string, reportType: ReportTypeId): SectionConfig {
  const def = getDefaultSectionConfigsForType(reportType).find(s => s.sectionId === sectionId);
  if (def) return { ...def };
  const page = getSectionsForType(reportType).find(s => s.sectionId === sectionId)?.page ?? 1;
  return { sectionId, type: 'builtin', visible: true, columnLayout: 1, page };
}

/**
 * Filter inapplicable ids and merge missing canonical registry entries,
 * preserving existing SectionConfig fields for ids already present.
 */
export function normalizeStudioSectionConfigs(
  reportType: ReportTypeId,
  sections: SectionConfig[],
): SectionConfig[] {
  const defaults = getDefaultSectionConfigsForType(reportType);
  const defaultById = new Map(defaults.map(s => [s.sectionId, s]));
  const existingById = new Map(sections.map(s => [s.sectionId, s]));
  const ids = sections.map(s => s.sectionId);
  const normalizedIds = projectTemplateSectionIds(reportType, ids, ids);
  return normalizedIds.map(id =>
    existingById.get(id) ?? defaultById.get(id) ?? fallbackBuiltinSection(id, reportType),
  );
}

function resolveRawSectionList(
  template: ReportTemplate | null,
  reportType: ReportTypeId,
  mode: StudioSectionListMode,
  filterReportType?: ReportTypeId | null,
): SectionConfig[] {
  if (!template) {
    return getDefaultSectionConfigsForType(reportType);
  }

  if (mode === 'per-type') {
    const rawEntry = template.sectionsByReportType?.[reportType];
    if (rawEntry !== undefined) {
      if (rawEntry === null) return [];
      const perType = coerceSectionConfigList(rawEntry);
      if (perType.length > 0) {
        return normalizeStudioSectionConfigs(reportType, perType);
      }
      if (Array.isArray(rawEntry) && rawEntry.length === 0) {
        return [];
      }
      // Corrupt or empty object entry — recover type defaults instead of crashing.
      return getDefaultSectionConfigsForType(reportType);
    }
    if (
      reportType === 'feasibility'
      && Array.isArray(template.sections)
      && template.sections.length > 0
    ) {
      // Normalize the legacy flat list the SAME way per-type lists are
      // normalized: preserve the user's saved order + per-section fields,
      // but back-fill any canonical feasibility section the saved array
      // predates (e.g. `capacityCreditDeepDive`, added in v1.2.0) at its
      // registry slot. Without this, an existing template saved before a
      // section was added could never position/hide that section in the
      // Studio (the per-type lists already reconcile via the path below).
      return normalizeStudioSectionConfigs(reportType, [...template.sections]);
    }
    return getDefaultSectionConfigsForType(reportType);
  }

  let sections: SectionConfig[];
  const coercedFlat = coerceSectionConfigList(template.sections);
  if (coercedFlat.length > 0) {
    sections = coercedFlat;
  } else {
    sections = getDefaultSectionConfigs();
  }

  if (filterReportType) {
    sections = filterFlatSectionsForType(sections, filterReportType, template);
    sections = normalizeStudioSectionConfigs(filterReportType, sections);
  }

  return sections;
}

export function resolveStudioSectionList(input: ResolveStudioSectionListInput): SectionConfig[] {
  const {
    template,
    reportType,
    mode,
    filterReportType,
    augmentSubSections: doAugment = false,
  } = input;

  let sections = resolveRawSectionList(template, reportType, mode, filterReportType);
  if (doAugment) {
    sections = augmentWithSubSections(sections);
  }
  return sections;
}

/** Ids in the resolved studio list (after optional sub-section augmentation). */
export function resolveStudioSectionIds(input: ResolveStudioSectionListInput): string[] {
  return resolveStudioSectionList(input).map(s => s.sectionId);
}
